import type {
  ConfirmationBranchConfig,
  EmailBranding,
  EmailTemplate,
  EmailTemplateType,
  FormEmailTemplates,
} from "@/lib/forms/email-templates";
import { DEFAULT_EMAIL_TEMPLATES, isConfirmationBranchComplete } from "@/lib/forms/email-templates";
import type { SavedCustomEmailTemplatesMap } from "@/lib/forms/email-saved-custom-templates";
import { coerceFilledFilesFormat } from "@/features/forms/_logic/constants/file-constants";

export const EMAIL_TEMPLATE_TYPES: EmailTemplateType[] = [
  "reminder",
  "confirmation",
  "confirmation_not_attending",
  "approval",
  "rejection",
  "invitation",
  "qr_code",
  "file_delivery",
];

export const SHARED_METADATA_FIELDS = ["sendFrom", "senderName", "replyTo", "cc", "sendToField"] as const;

export type SharedMetadataField = (typeof SHARED_METADATA_FIELDS)[number];

/** Sender, routing, and visual branding applied to every email type by default. */
export interface SharedEmailSettings {
  sendFrom?: string;
  senderName?: string;
  /** One or more reply-to addresses, comma-separated (same convention as `cc`); 2+ send as `reply_to_list`. */
  replyTo?: string;
  cc?: string;
  sendToField?: string;
  branding?: EmailBranding;
}

/** Per-template overrides when "Customize for this email type" is enabled. */
export interface EmailTemplateOverride {
  useCustomSettings?: boolean;
  sendFrom?: string;
  senderName?: string;
  /** One or more reply-to addresses, comma-separated (same convention as `cc`); 2+ send as `reply_to_list`. */
  replyTo?: string;
  cc?: string;
  sendToField?: string;
  branding?: EmailBranding;
}

export type EmailTemplatesRecord = FormEmailTemplates & {
  shared_settings?: SharedEmailSettings;
  template_overrides?: Partial<Record<EmailTemplateType, EmailTemplateOverride>>;
  /** When set, routes declining submissions to the `confirmation_not_attending` template. */
  confirmation_branch?: ConfirmationBranchConfig;
  calendar_invite_settings?: unknown;
  /** Unsaved custom email scratch pad. */
  custom_draft?: EmailTemplate;
  /** @deprecated Use custom_draft — kept for backward compatibility. */
  custom?: EmailTemplate;
  saved_custom_templates?: SavedCustomEmailTemplatesMap;
};

const pickBrandingFromTemplates = (raw: EmailTemplatesRecord | null | undefined): EmailBranding | undefined => {
  if (!raw) return undefined;
  const merged: EmailBranding = {};
  for (const type of EMAIL_TEMPLATE_TYPES) {
    const b = raw[type]?.branding;
    if (!b) continue;
    Object.assign(merged, b);
  }
  return Object.keys(merged).length > 0 ? merged : undefined;
};

/**
 * Loads shared settings from storage, or infers from existing per-template data.
 */
export const deriveSharedSettings = (raw: EmailTemplatesRecord | null | undefined): SharedEmailSettings => {
  if (raw?.shared_settings) {
    return raw.shared_settings;
  }

  const preferredTypes: EmailTemplateType[] = ["invitation", "reminder", "confirmation", "approval"];
  for (const type of preferredTypes) {
    const tmpl = raw?.[type];
    if (!tmpl) continue;
    if (tmpl.senderName || tmpl.sendFrom || tmpl.replyTo || tmpl.sendToField || tmpl.branding) {
      return {
        sendFrom: tmpl.sendFrom ?? DEFAULT_EMAIL_TEMPLATES[type].sendFrom,
        senderName: tmpl.senderName ?? DEFAULT_EMAIL_TEMPLATES[type].senderName,
        replyTo: tmpl.replyTo ?? DEFAULT_EMAIL_TEMPLATES[type].replyTo,
        cc: tmpl.cc,
        sendToField: tmpl.sendToField,
        branding: pickBrandingFromTemplates(raw),
      };
    }
  }

  return {
    sendFrom: DEFAULT_EMAIL_TEMPLATES.invitation.sendFrom,
    senderName: DEFAULT_EMAIL_TEMPLATES.invitation.senderName,
    replyTo: DEFAULT_EMAIL_TEMPLATES.invitation.replyTo,
  };
};

export const deriveTemplateOverrides = (
  raw: EmailTemplatesRecord | null | undefined
): Partial<Record<EmailTemplateType, EmailTemplateOverride>> => raw?.template_overrides ?? {};

/**
 * Merges shared settings, optional per-type override, and template subject/body.
 */
export const resolveEffectiveEmailTemplate = (
  type: EmailTemplateType,
  content: EmailTemplate,
  shared: SharedEmailSettings,
  overrides: Partial<Record<EmailTemplateType, EmailTemplateOverride>>
): EmailTemplate => {
  const defaults = DEFAULT_EMAIL_TEMPLATES[type];
  const override = overrides[type];
  const useCustom = override?.useCustomSettings === true;

  if (useCustom) {
    return {
      subject: content.subject,
      body: content.body,
      sendFrom: override.sendFrom ?? shared.sendFrom ?? defaults.sendFrom,
      senderName: override.senderName ?? shared.senderName ?? defaults.senderName,
      replyTo: override.replyTo ?? shared.replyTo ?? defaults.replyTo,
      cc: override.cc ?? shared.cc,
      sendToField: override.sendToField ?? shared.sendToField,
      includeCalendarInvite: content.includeCalendarInvite,
      branding: { ...shared.branding, ...override.branding },
      attachFormat: content.attachFormat,
    };
  }

  return {
    subject: content.subject,
    body: content.body,
    sendFrom: shared.sendFrom ?? content.sendFrom ?? defaults.sendFrom,
    senderName: shared.senderName ?? content.senderName ?? defaults.senderName,
    replyTo: shared.replyTo ?? content.replyTo ?? defaults.replyTo,
    cc: shared.cc ?? content.cc,
    sendToField: shared.sendToField ?? content.sendToField,
    includeCalendarInvite: content.includeCalendarInvite,
    branding: { ...content.branding, ...shared.branding },
    attachFormat: content.attachFormat,
  };
};

/**
 * Builds the JSONB payload for save (shared block + materialized per-type templates).
 */
export const serializeEmailTemplatesForSave = (
  editedTemplates: Record<string, EmailTemplate>,
  shared: SharedEmailSettings,
  overrides: Partial<Record<EmailTemplateType, EmailTemplateOverride>>,
  savedCustomTemplates?: SavedCustomEmailTemplatesMap,
  /** The form's current raw record. Sidecar keys not editable through the per-type template UI
   *  (calendar_invite_settings, and confirmation_branch when the caller doesn't manage it) are
   *  carried forward from it so a save doesn't silently wipe them. */
  existing?: EmailTemplatesRecord | null,
  /** Editable RSVP branch config from the editor. When `undefined` (caller doesn't manage it) the
   *  existing config is carried forward untouched. When provided, it wins: a complete config is
   *  persisted, and `null` or an incomplete one clears branching. */
  confirmationBranch?: ConfirmationBranchConfig | null
): EmailTemplatesRecord => {
  const result: EmailTemplatesRecord = {
    shared_settings: shared,
    template_overrides: overrides,
    saved_custom_templates: savedCustomTemplates ?? {},
  };

  // RSVP branch config. When the caller manages it (arg is not undefined) persist only a complete
  // config — an incomplete one (question or answers missing) would match nothing, so treat it as
  // "off". When the caller doesn't manage it, carry the stored config forward so persisting an
  // unrelated template edit doesn't wipe it (without this, the first "Save" would stop branching).
  const resolvedBranch =
    confirmationBranch !== undefined
      ? isConfirmationBranchComplete(confirmationBranch)
        ? (confirmationBranch ?? undefined)
        : undefined
      : existing?.confirmation_branch;
  if (resolvedBranch) result.confirmation_branch = resolvedBranch;
  if (existing?.calendar_invite_settings !== undefined) {
    result.calendar_invite_settings = existing.calendar_invite_settings;
  }

  if (editedTemplates.custom?.subject || editedTemplates.custom?.body) {
    result.custom_draft = editedTemplates.custom;
  }

  for (const type of EMAIL_TEMPLATE_TYPES) {
    const content = editedTemplates[type];
    if (!content?.subject && !content?.body) continue;
    // Don't materialize the not-attending default onto forms that never opted into RSVP
    // branching — it would just be unused bloat on every form row.
    if (type === "confirmation_not_attending" && !result.confirmation_branch) continue;

    const effective = resolveEffectiveEmailTemplate(type, content, shared, overrides);
    result[type] = {
      subject: content.subject,
      body: content.body,
      sendFrom: effective.sendFrom,
      senderName: effective.senderName,
      replyTo: effective.replyTo,
      cc: effective.cc,
      sendToField: effective.sendToField,
      includeCalendarInvite: content.includeCalendarInvite,
      branding: effective.branding,
      // file_delivery carries an extra attach-format choice; preserve it on save so
      // the Emails-tab "Save" doesn't clobber it (other types ignore the field).
      // Coerce so only allowlisted values land in the stored JSONB.
      ...(type === "file_delivery" && content.attachFormat
        ? { attachFormat: coerceFilledFilesFormat(content.attachFormat) }
        : {}),
    };
  }

  return result;
};

export const usesCustomSettings = (
  type: EmailTemplateType,
  overrides: Partial<Record<EmailTemplateType, EmailTemplateOverride>>
): boolean => overrides[type]?.useCustomSettings === true;
